import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Bot, Play, Pause, Power, PowerOff, ChevronDown, Loader2,
} from "lucide-react";
import api, { apiError } from "../lib/api";

const CAT_LABELS = {
  master: "Orchestrator",
  source: "Lead Source",
  enrichment: "Enrichment & Identity",
  valuation: "Valuation",
  qualification: "Qualification",
  motivation: "Seller Motivation",
  identity: "Deduplication",
  freshness: "Freshness",
  outreach: "Outreach",
};
const CAT_ORDER = ["source", "enrichment", "valuation", "qualification", "motivation", "identity", "freshness", "outreach"];

const STATUS_STYLE = {
  RUNNING: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  COMPLETED: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  WAITING: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  MANUAL: "bg-orange-500/10 text-orange-500 border-orange-500/30",
  DISABLED: "bg-muted text-muted-foreground border-border",
  ERROR: "bg-destructive/10 text-destructive border-destructive/30",
  IDLE: "bg-primary/10 text-primary border-primary/30",
};
const STATUS_LABEL = { MANUAL: "MANUAL / INTEGRATION REQUIRED" };

function StatusBadge({ status, live }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap inline-flex items-center gap-1 ${live ? "bg-emerald-500/12 text-emerald-500 border-emerald-500/40" : (STATUS_STYLE[status] || STATUS_STYLE.WAITING)}`} data-testid="agent-status-badge">
      {live && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />}
      {live ? "RUNNING" : (STATUS_LABEL[status] || status)}
    </span>
  );
}

function fmtRun(iso) {
  if (!iso) return "Never";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function AgentRow({ agent, onAction, busy, live }) {
  const [open, setOpen] = useState(false);
  const disabled = agent.status === "DISABLED";
  return (
    <div className={`rounded-xl border bg-card transition-colors ${live ? "border-emerald-500/50 ring-1 ring-emerald-500/30" : "border-border"}`} data-testid={`agent-card-${agent.agent_id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 p-4 text-left" data-testid={`agent-toggle-${agent.agent_id}`} aria-expanded={open}>
        <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 relative">
          <Bot className="h-4 w-4 text-primary" />
          {live && (
            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5" data-testid={`agent-live-${agent.agent_id}`}>
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{agent.name}</span>
            <span className="text-xs text-muted-foreground">· {agent.title}</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {live ? <span className="text-emerald-500 font-medium">Working now…</span> : <>Last run {fmtRun(agent.last_run)} · {agent.records_scanned || 0} scanned · {agent.leads_found || 0} leads</>}
          </div>
        </div>
        <StatusBadge status={live ? "RUNNING" : agent.status} live={live} />
        <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0 pl-16 space-y-3" data-testid={`agent-body-${agent.agent_id}`}>
          <div>
            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-1">Objective</div>
            <p className="text-sm text-foreground/90">{agent.objective}</p>
          </div>
          <div className="grid grid-cols-1 gap-3 text-xs">
            <div><span className="text-muted-foreground">Sources: </span>{(agent.sources || []).join(", ")}</div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={() => onAction(agent, "run")} disabled={busy || disabled} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border hover:border-primary/50 disabled:opacity-40" data-testid={`agent-run-${agent.agent_id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Run
            </button>
            <button onClick={() => onAction(agent, "pause")} disabled={busy || disabled} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border hover:border-primary/50 disabled:opacity-40" data-testid={`agent-pause-${agent.agent_id}`}>
              <Pause className="h-3.5 w-3.5" /> Pause
            </button>
            {disabled ? (
              <button onClick={() => onAction(agent, "enable")} disabled={busy} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border hover:border-primary/50 disabled:opacity-40" data-testid={`agent-enable-${agent.agent_id}`}>
                <Power className="h-3.5 w-3.5" /> Enable
              </button>
            ) : (
              <button onClick={() => onAction(agent, "disable")} disabled={busy} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border hover:border-destructive/50 text-muted-foreground disabled:opacity-40" data-testid={`agent-disable-${agent.agent_id}`}>
                <PowerOff className="h-3.5 w-3.5" /> Disable
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AgentCommandCenter() {
  const [master, setMaster] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [runningIds, setRunningIds] = useState(new Set());
  const [showLegacy, setShowLegacy] = useState(false);

  const load = () => {
    api.get("/acq/agents").then((r) => {
      setMaster(r.data.master);
      setAgents(r.data.agents || []);
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { document.title = "ARQITEK — Agent Command Center"; load(); }, []);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/acq/activity");
        if (alive) setRunningIds(new Set((data.running || []).map((a) => a.agent_id)));
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const onAction = async (agent, action) => {
    setBusyId(agent.agent_id);
    try {
      const { data } = await api.post(`/acq/agents/${agent.agent_id}/action`, { action });
      if (!data.ok) { toast.error(data.error || "Action failed"); return; }
      setAgents((prev) => prev.map((a) => (a.agent_id === agent.agent_id ? { ...a, ...data.agent } : a)));
      toast.success(`${agent.name}: ${action}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="agent-command-center-page">
      <Link to="/ai-office" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="acc-back">
        <ArrowLeft className="h-4 w-4" /> AI Office
      </Link>
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">Acquisition Intelligence</span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Agent Command Center</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Agents you create to run the acquisition pipeline appear here — status, purpose and controls.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading agents…</div>
      ) : (
        <>
          {master && (
            <div className="rounded-xl border border-primary/30 bg-primary/[0.04] p-4" data-testid="agent-master-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0"><Bot className="h-5 w-5 text-primary" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{master.name}</span>
                    <span className="text-xs text-muted-foreground">· Land Development Analyst</span>
                    <StatusBadge status={master.status} live={runningIds.has("angela")} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Directs the pipeline and analyzes known, GIS-verified parcels. She no longer performs broad
                    inventory discovery.
                  </p>
                </div>
              </div>
            </div>
          )}

          {agents.length === 0 && !master ? (
            <div className="rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center" data-testid="agents-empty-state">
              <Bot className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
              <div className="font-semibold">No agents yet</div>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                The roster has been cleared for a clean slate. Create a new agent when you're ready and it will appear here.
              </p>
            </div>
          ) : (() => {
            const active = agents.filter((a) => a.active_in_architecture);
            const legacy = agents.filter((a) => !a.active_in_architecture);
            return (
              <>
                <div className="text-xs uppercase tracking-[0.14em] text-primary pt-2" data-testid="active-agents-header">
                  Active in current architecture <span className="text-primary/60">({active.length})</span>
                </div>
                {CAT_ORDER.map((cat) => {
                  const list = active.filter((a) => a.category === cat);
                  if (!list.length) return null;
                  return (
                    <div key={cat} className="space-y-2">
                      <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground pt-2">
                        {CAT_LABELS[cat]} <span className="text-muted-foreground/60">({list.length})</span>
                      </div>
                      {list.map((a) => (
                        <AgentRow key={a.agent_id} agent={a} onAction={onAction} busy={busyId === a.agent_id} live={runningIds.has(a.agent_id)} />
                      ))}
                    </div>
                  );
                })}

                {legacy.length > 0 && (
                  <div className="pt-4">
                    <button
                      onClick={() => setShowLegacy((v) => !v)}
                      className="w-full flex items-center justify-between rounded-xl border border-dashed border-border bg-muted/20 px-4 py-3 text-left hover:border-primary/40 transition-colors"
                      data-testid="legacy-agents-toggle"
                    >
                      <div>
                        <div className="text-sm font-semibold">Legacy agents — not used in the current architecture</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {legacy.length} agents from the retired broad-discovery / AI-enrichment model. Kept for reference; dormant.
                        </div>
                      </div>
                      <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${showLegacy ? "rotate-180" : ""}`} />
                    </button>
                    {showLegacy && (
                      <div className="mt-3 space-y-2 opacity-70" data-testid="legacy-agents-list">
                        {CAT_ORDER.map((cat) => {
                          const list = legacy.filter((a) => a.category === cat);
                          if (!list.length) return null;
                          return (
                            <div key={cat} className="space-y-2">
                              <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground pt-2">
                                {CAT_LABELS[cat]} <span className="text-muted-foreground/60">({list.length})</span>
                              </div>
                              {list.map((a) => (
                                <AgentRow key={a.agent_id} agent={a} onAction={onAction} busy={busyId === a.agent_id} live={runningIds.has(a.agent_id)} />
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
